// Pivots: fully determined queries from a known anchor into a target entity.
//
// Once an investigation holds an anchor, most of the next query is not a
// choice: the id field to match on, the entity's time field, its window
// and row bounds, whether its search field self-wraps, and what is worth
// selecting. The model kept getting exactly those parts wrong while the
// ids were always right. A pivot takes (anchor, target entity, optional
// term and window) and returns the spec, built from the live schema's own
// field names, so the correct query is the easy one and the model learns
// the shape from the spec it gets back.

import { NODE_KINDS, neighbours, nodesOf } from "./investigationGraph.js";

const F = "DIMENSION_FIELD_";
const E = "ENTITY_TYPE_";
const DAY = 86400;

export const OS_WINDOW_MAX_SECONDS = 30 * DAY;
export const DEFAULT_WINDOW_MAX_SECONDS = 90 * DAY;
export const TEXT_VIEW_ROW_BOUND = 200;
export const RECORD_ROW_BOUND = 1000;
export const DEFAULT_LIMIT = 200;

// Which id fields on a target entity an anchor of each kind can match.
// Session anchors remember which id space produced them (trace, session,
// or notification id) and match only that one; the spaces differ.
const ANCHOR_ID_SUFFIXES = {
    endpoint: ["_ENDPOINT_ID"],
    session: ["_TRACE_ID", "_SESSION_ID", "_NOTIFICATION_ID"],
    process: ["_PROCESS_START_KEY"],
    message: ["_MESSAGE_ID"],
    person: ["_ACCOUNT_NAME", "_REGISTERED_USER", "_PRIMARY_USER"],
};
const SESSION_SUFFIX_BY_ID_KIND = { trace: "_TRACE_ID", session: "_SESSION_ID", notification: "_NOTIFICATION_ID" };

// Entities whose window is bounded at 30 days: OS telemetry and the text
// search views. Everything else takes 90.
const OS_FAMILY = /^(PROCESS_EVENT|TELEMETRY_TIMELINE|DNS_EVENT|FILE_EVENT|NETWORK_CONNECTION|TRACE_OS_ROLLUP|CONVERSATION_SEARCH_CONTENT|PROMPT_SEARCH)$/;
// Text search views: 200-row bound, need a LIKE term, and their search
// field wraps the term in wildcards itself.
const TEXT_VIEWS = {
    CONVERSATION_SEARCH_CONTENT: { field: "CONVERSATION_SEARCH_TEXT", selfWraps: true },
    TELEMETRY_TIMELINE: { field: "TELEMETRY_TIMELINE_SEARCH_TEXT", selfWraps: true },
};
// Where a plain term goes on the record entities.
const TERM_FIELDS = {
    PROCESS_EVENT: "PROCESS_EVENT_COMMAND_LINE",
    TRACE_TOOL_ACTIVITY: "TRACE_TOOL_INPUT",
    DNS_EVENT: "DNS_EVENT_QUERY_NAME",
    FILE_EVENT: "FILE_EVENT_PRIMARY_PATH",
    PROMPT_SEARCH: "PROMPT_SEARCH_SUMMARY",
    PROMPT_ANALYSIS: "PROMPT_ANALYSIS_SUMMARY",
    AI_AGENT_EXECUTION: "AI_AGENT_EXECUTION_COMMAND_LINE",
    TRACE_OS_ROLLUP: "TRACE_OS_ROLLUP_COMMAND_LINES",
};
// Trace-detail entities: only reachable from a session anchor, by trace id.
const TRACE_DETAIL = /^(CONVERSATION_MESSAGE|CONVERSATION_MESSAGE_PART|CONVERSATION_TOOL_CALL|CONVERSATION_TOOL_RESULT|MODEL_INVOCATION|CONVERSATION_SOURCE_OBSERVATION|TRACE_ACTIVATION|CONVERSATION_FINGERPRINT)$/;
// Conversation entities are windowed by the trace's start, so a window
// around the action misses them; scoped by trace id they are cheap, so
// take the widest window instead.
const TRACE_START_WINDOWED = /^(CONVERSATION_|MODEL_INVOCATION|TRACE_ACTIVATION)/;
// Aggregate entities need a metric to carry a time filter; pivots into
// them are not record reads and are not offered.
const NOT_PIVOTABLE = /^(AI_SESSION|TOKEN|ILO_|IDENTITY_COUNTS|ENDPOINT_STATUS|AI_SERVICE_OBSERVATION_ENDPOINT_RECENCY)/;

// The pivots worth offering, by what they prove. Everything else in the
// schema is still reachable with a hand-written analytics_query.
export const PIVOT_TARGETS = Object.freeze({
    execution: ["PROCESS_EVENT", "TELEMETRY_TIMELINE", "TRACE_TOOL_ACTIVITY", "DNS_EVENT", "FILE_EVENT", "NETWORK_CONNECTION"],
    conversation: ["CONVERSATION_TRACE", "CONVERSATION_MESSAGE", "CONVERSATION_TOOL_CALL", "CONVERSATION_SEARCH_CONTENT"],
    prompt: ["PROMPT_SEARCH", "AI_AGENT_EXECUTION"],
    directory: ["ENDPOINT", "ENDPOINT_USER_ASSIGNMENT", "DIRECTORY_USER"],
});
const OFFERED = new Set(Object.values(PIVOT_TARGETS).flat());

const LABEL_FIELD = /(COMMAND_LINE|IMAGE_NAME|ACCOUNT_NAME|HOSTNAME|REGISTERED_USER|TOOL_NAME|TOOL_INPUT|PART_KIND|_ROLE|SNIPPET|_SUMMARY|PROVIDER|_MODEL|QUERY_NAME|PRIMARY_PATH|REMOTE_ADDRESS|REMOTE_PORT_TEXT|_FAMILY|EVENT_KIND|_LABEL|CLIENT_SURFACE|DISPLAY_NAME|_EMAIL|_NAME|MESSAGE_AT|COMPLETENESS)$/;
const ID_FIELD = /(_ENDPOINT_ID|_TRACE_ID|_SESSION_ID|_NOTIFICATION_ID|_PROCESS_START_KEY|_MESSAGE_ID)$/;
const NOISE_FIELD = /(_TRUNCATED|_BYTES|_SHA256|_VERSION|_ORDINAL|_SEQUENCE|SOURCE_EVENT_ID|_CAPTURE|RELEVANCE|ATTRIBUTION_METHOD|DOMAIN_NAME|PACKAGE_NAME|RELATIVE_APP_NAME|CREATE_TIME|PARENT_|CREATOR_|_PID$|PROCESS_ID$|_KEY$)/;

function short(name) {
    return String(name || "").replace(F, "").replace(E, "");
}

export function entityIndex(schema) {
    const byName = new Map();
    for (const e of schema?.entities || []) {
        const name = short(e.entity || e.name);
        byName.set(name, {
            name,
            timeField: e.timeField || e.time_field || null,
            fields: (e.fields || []).map((f) => short(typeof f === "string" ? f : (f.field || f.name))),
            // Live schemas type their fields (SCALAR_TYPE_UUID, _INT, ...);
            // the names-only fixture leaves the map empty.
            types: new Map((e.fields || []).filter((f) => f && typeof f === "object" && f.type).map((f) => [short(f.field || f.name), f.type])),
        });
    }
    return byName;
}

// Field types that cannot hold an id string. PROCESS_EVENT_SESSION_ID is an
// integer OS session, not the agent session, and a match on it fails
// with a type error; when a schema carries no types this list stands in.
const NOT_ID_TYPES = /^SCALAR_TYPE_(INT|UINT|INT32|INT64|UINT32|UINT64|FLOAT|DOUBLE|BOOL)/;
const KNOWN_NON_ID_FIELDS = new Set(["PROCESS_EVENT_SESSION_ID"]);

function canHoldId(entity, field) {
    if (KNOWN_NON_ID_FIELDS.has(field)) return false;
    const type = entity.types?.get(field);
    return !type || !NOT_ID_TYPES.test(type);
}

// A session anchor matches the field of its own id kind first, then any
// other uuid-typed trace/session/notification field: the three ids coincide
// for most providers, and a miss returns no rows rather than an error.
function idFieldFor(entity, anchor) {
    const own = anchor.kind === "session" ? SESSION_SUFFIX_BY_ID_KIND[anchor.attrs?.idKind] : null;
    const suffixes = [...new Set([own, ...(ANCHOR_ID_SUFFIXES[anchor.kind] || [])].filter(Boolean))];
    for (const suffix of suffixes) {
        const hit = entity.fields.find((f) => f.endsWith(suffix) && canHoldId(entity, f));
        if (hit) return hit;
    }
    return null;
}

export function windowBoundFor(entityName) {
    return OS_FAMILY.test(entityName) ? OS_WINDOW_MAX_SECONDS : DEFAULT_WINDOW_MAX_SECONDS;
}

export function rowBoundFor(entityName) {
    return TEXT_VIEWS[entityName] ? TEXT_VIEW_ROW_BOUND : RECORD_ROW_BOUND;
}

// Targets an anchor of this kind can pivot into, from the schema itself:
// the offered entities that carry a matching id field.
export function pivotsFrom(schema, anchor) {
    const index = schema instanceof Map ? schema : entityIndex(schema);
    const out = [];
    for (const name of OFFERED) {
        const entity = index.get(name);
        if (!entity || NOT_PIVOTABLE.test(name)) continue;
        if (TRACE_DETAIL.test(name) && anchor.kind !== "session") continue;
        const idField = idFieldFor(entity, anchor);
        if (!idField) continue;
        out.push({ entity: name, idField, needsTerm: !!TEXT_VIEWS[name], termField: TEXT_VIEWS[name]?.field || TERM_FIELDS[name] || null });
    }
    return out;
}

// The window an anchor was observed in, from the graph's window anchors.
function observedWindow(graph, anchor) {
    let start = null;
    let end = null;
    for (const nb of neighbours(graph, anchor.key)) {
        if (nb.node.kind !== "window") continue;
        const [s, e] = nb.node.id.split("/");
        const sMs = Date.parse(s);
        const eMs = Date.parse(e);
        if (!Number.isFinite(sMs) || !Number.isFinite(eMs)) continue;
        start = start == null ? sMs : Math.min(start, sMs);
        end = end == null ? eMs : Math.max(end, eMs);
    }
    return start == null ? null : { start, end };
}

function iso(ms) {
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

// Resolve the window: an explicit absolute window, else the anchor's
// observed window padded, else the widest the entity allows. Windows are
// clamped to the entity's bound from the end backwards.
export function resolveWindow({ entityName, anchor, graph, window = null, padSeconds = 300, now = Date.now() }) {
    const bound = windowBoundFor(entityName);
    const notes = [];
    // Models often hand the window over as a JSON string, or in the spec's
    // own { absolute: { start, end } } shape; both mean the same thing.
    if (typeof window === "string" && window.trim().startsWith("{")) {
        try { window = JSON.parse(window); } catch { throw new Error("window must be { start, end } in RFC 3339 UTC or a duration like \"7d\""); }
    }
    if (window && typeof window === "object" && window.absolute && typeof window.absolute === "object") window = window.absolute;
    // Conversation-side entities are windowed by the trace's start, so a
    // window around the moment in question misses every trace that began
    // earlier. When an id scopes the query the window adds nothing; use the
    // widest one and say so, even if the caller passed a narrower window.
    const idScoped = anchor && ["session", "process", "message"].includes(anchor.kind);
    if (TRACE_START_WINDOWED.test(entityName) && (idScoped || !(window && typeof window === "object" && window.start))) {
        notes.push(`${entityName} rows carry the trace's start time, so the widest window (${bound / DAY} days) is used and the ${anchor?.kind || "anchor"} id does the scoping${window ? "; the window given was not applied" : ""}`);
        return { timeRange: { relative: { durationSeconds: String(bound) } }, notes };
    }
    if (window && typeof window === "object" && window.start && window.end) {
        let s = Date.parse(window.start);
        const e = Date.parse(window.end);
        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) throw new Error("window needs RFC 3339 start and end with end after start");
        if ((e - s) / 1000 > bound) { s = e - bound * 1000; notes.push(`window clamped to the entity's ${bound / DAY}-day bound`); }
        return { timeRange: { absolute: { start: iso(s), end: iso(e) } }, notes };
    }
    if (typeof window === "string" && /^\d+d$/.test(window)) {
        const seconds = Math.min(Number(window.slice(0, -1)) * DAY, bound);
        return { timeRange: { relative: { durationSeconds: String(seconds) } }, notes };
    }
    const seen = graph ? observedWindow(graph, anchor) : null;
    if (seen) {
        let s = seen.start - padSeconds * 1000;
        const e = seen.end + padSeconds * 1000;
        if ((e - s) / 1000 > bound) { s = e - bound * 1000; notes.push(`window clamped to the entity's ${bound / DAY}-day bound`); }
        notes.push("window taken from where the anchor was observed, padded 5 minutes each side");
        return { timeRange: { absolute: { start: iso(s), end: iso(e) } }, notes };
    }
    notes.push(`no window given and none observed for the anchor; using the entity's ${bound / DAY}-day bound`);
    return { timeRange: { relative: { durationSeconds: String(bound) } }, notes };
}

function defaultDimensions(entity) {
    const picked = [];
    const push = (f) => { if (f && !picked.includes(f) && entity.fields.includes(f)) picked.push(f); };
    push(short(entity.timeField));
    for (const f of entity.fields) if (ID_FIELD.test(f) && !/(PARENT_|CREATOR_)/.test(f)) push(f);
    for (const f of entity.fields) if (LABEL_FIELD.test(f) && !NOISE_FIELD.test(f)) push(f);
    return picked.slice(0, 10);
}

function alias(field, entityName) {
    return field.replace(entityName + "_", "").toLowerCase();
}

// Build the spec. Throws with a message meant for the model when the
// pivot is not legal or is missing what the entity requires.
export function pivotSpec({ schema, graph = null, anchor, to, term = null, window = null, limit = null, fields = null, now = Date.now() }) {
    const index = schema instanceof Map ? schema : entityIndex(schema);
    const entityName = short(to).toUpperCase();
    const entity = index.get(entityName);
    if (!entity) throw new Error(`unknown entity ${entityName}`);
    if (!OFFERED.has(entityName) || NOT_PIVOTABLE.test(entityName)) {
        throw new Error(`${entityName} is not a pivot target; write an analytics_query for it. Pivot targets: ${[...OFFERED].join(", ")}`);
    }
    if (TRACE_DETAIL.test(entityName) && anchor.kind !== "session") {
        throw new Error(`${entityName} is trace-detail: it needs a session anchor (trace id), not ${anchor.kind}`);
    }
    const idField = idFieldFor(entity, anchor);
    if (!idField) {
        const legal = pivotsFrom(index, anchor).map((p) => p.entity);
        throw new Error(`${entityName} has no field that matches a ${anchor.kind} anchor. From ${anchor.key} you can pivot into: ${legal.join(", ") || "nothing offered"}`);
    }
    const textView = TEXT_VIEWS[entityName];
    const cleanTerm = typeof term === "string" ? term.trim() : "";
    if (textView && cleanTerm.replace(/%/g, "").length < 3) {
        throw new Error(`${entityName} is a text search view: pass a term of at least 3 characters (it is matched case-insensitively; wildcards are added for you)`);
    }
    const stepFilter = [{ pred: { field: { stepAlias: "r", field: F + idField }, op: "OPERATOR_EQ", value: String(anchor.id) } }];
    if (cleanTerm) {
        const termField = textView?.field || TERM_FIELDS[entityName];
        if (!termField) throw new Error(`${entityName} has no text field to search; drop the term or pick one of its fields with an analytics_query`);
        const value = textView?.selfWraps ? cleanTerm.replace(/^%+|%+$/g, "") : (cleanTerm.includes("%") ? cleanTerm : `%${cleanTerm}%`);
        stepFilter.push({ pred: { field: { stepAlias: "r", field: F + termField }, op: "OPERATOR_ILIKE", value } });
    }
    const { timeRange, notes } = resolveWindow({ entityName, anchor, graph, window, now });
    const own = anchor.kind === "session" ? SESSION_SUFFIX_BY_ID_KIND[anchor.attrs?.idKind] : null;
    if (own && !idField.endsWith(own)) notes.push(`matched on ${idField}; the anchor was first seen as a ${anchor.attrs.idKind} id and the two usually coincide, so no rows means the ids differ here`);
    const bound = rowBoundFor(entityName);
    const wanted = Array.isArray(fields) && fields.length ? fields.map((f) => short(f).toUpperCase()).filter((f) => entity.fields.includes(f)) : defaultDimensions(entity);
    if (!wanted.length) throw new Error(`none of the requested fields exist on ${entityName}`);
    const dimensions = wanted.map((f) => ({ field: { stepAlias: "r", field: F + f }, outputAlias: alias(f, entityName), fillBuckets: false }));
    const timeShort = short(entity.timeField);
    const orderBy = timeShort && wanted.includes(timeShort) ? [{ outputAlias: alias(timeShort, entityName), direction: "DIRECTION_DESC" }] : [];
    const requested = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Math.floor(Number(limit)) : DEFAULT_LIMIT;
    if (requested > bound) notes.push(`limit clamped to the entity's ${bound}-row bound`);
    const spec = {
        root: { alias: "r", entity: E + entityName, stepFilter, subOrder: [], children: [] },
        filter: null,
        output: { dimensions, metrics: [] },
        orderBy,
        limit: Math.min(requested, bound),
        timeRange,
        offset: 0,
    };
    return { spec, entity: entityName, idField, notes };
}

// One line per anchor kind present in the graph: where it can go from
// here. Rendered under the graph so the moves sit next to the anchors.
// A fleet-wide text search over one entity, the first move of a recipe:
// the entity's own term field, the window given (default the last day,
// since a scan over the full bound reads past the execution ceiling),
// default fields, newest first. No anchor: the term does the scoping.
export const SEARCH_DEFAULT_WINDOW_SECONDS = DAY;
export function searchableEntities(schema) {
    const index = schema instanceof Map ? schema : entityIndex(schema);
    return [...index.keys()].filter((name) => {
        const field = TEXT_VIEWS[name]?.field || TERM_FIELDS[name];
        return field && index.get(name).fields.includes(field);
    });
}
export function searchSpec({ schema, entity, term, window = null, limit = null, now = Date.now() }) {
    const index = schema instanceof Map ? schema : entityIndex(schema);
    const entityName = short(entity).toUpperCase();
    const def = index.get(entityName);
    if (!def) throw new Error(`unknown entity ${entityName}`);
    const textView = TEXT_VIEWS[entityName];
    const termField = textView?.field || TERM_FIELDS[entityName];
    if (!termField || !def.fields.includes(termField)) throw new Error(`${entityName} has no text field to search; searchable: ${searchableEntities(index).join(", ")}`);
    const cleanTerm = typeof term === "string" ? term.trim() : "";
    if (cleanTerm.replace(/%/g, "").length < 3) throw new Error("search needs a term of at least 3 characters");
    const value = textView?.selfWraps ? cleanTerm.replace(/^%+|%+$/g, "") : (cleanTerm.includes("%") ? cleanTerm : `%${cleanTerm}%`);
    let timeRange;
    let notes = [];
    if (window) {
        ({ timeRange, notes } = resolveWindow({ entityName, anchor: null, graph: null, window, now }));
    } else {
        timeRange = { relative: { durationSeconds: String(Math.min(SEARCH_DEFAULT_WINDOW_SECONDS, windowBoundFor(entityName))) } };
        notes.push("no window given; searched the last 24 hours");
    }
    const bound = rowBoundFor(entityName);
    const wanted = defaultDimensions(def);
    const dimensions = wanted.map((f) => ({ field: { stepAlias: "s", field: F + f }, outputAlias: alias(f, entityName), fillBuckets: false }));
    const timeShort = short(def.timeField);
    const orderBy = timeShort && wanted.includes(timeShort) ? [{ outputAlias: alias(timeShort, entityName), direction: "DIRECTION_DESC" }] : [];
    const requested = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Math.floor(Number(limit)) : DEFAULT_LIMIT;
    if (requested > bound) notes.push(`limit clamped to the entity's ${bound}-row bound`);
    const spec = {
        root: { alias: "s", entity: E + entityName, stepFilter: [{ pred: { field: { stepAlias: "s", field: F + termField }, op: "OPERATOR_ILIKE", value } }], subOrder: [], children: [] },
        filter: null,
        output: { dimensions, metrics: [] },
        orderBy,
        limit: Math.min(requested, bound),
        timeRange,
        offset: 0,
    };
    return { spec, entity: entityName, field: termField, notes };
}

export function termFieldFor(entityName) {
    const name = short(entityName).toUpperCase();
    return TEXT_VIEWS[name]?.field || TERM_FIELDS[name] || null;
}

export function renderAffordances(schema, graph) {
    const index = schema instanceof Map ? schema : entityIndex(schema);
    const lines = [];
    for (const kind of NODE_KINDS) {
        const sample = nodesOf(graph, kind)[0];
        if (!sample) continue;
        const targets = pivotsFrom(index, sample);
        if (!targets.length) continue;
        lines.push(`  ${kind} -> ${targets.map((p) => p.entity + (p.needsTerm ? " (term)" : "")).join(", ")}`);
    }
    return lines.length ? ["pivots available (pivot { from: <anchor key>, to: <entity>, term?, window? }):", ...lines].join("\n") : "";
}

// Anchors of the target's kind already linked to `from` and produced by
// the target entity: what a pivot would re-fetch.
// The rules a hand-written query against this entity has to satisfy, as
// pivot applies them. Appended to a failing analytics_query so the model
// reads the rule next to the error rather than rediscovering it.
export function entityRules(entityName) {
    const name = short(entityName).toUpperCase();
    if (!OFFERED.has(name) || NOT_PIVOTABLE.test(name)) return [];
    const lines = [`window at most ${windowBoundFor(name) / DAY} days`, `at most ${rowBoundFor(name)} rows`];
    const view = TEXT_VIEWS[name];
    if (view) lines.push(`text search needs an ILIKE predicate on ${view.field} with a term of at least 3 characters (the view adds the wildcards)`);
    if (TRACE_START_WINDOWED.test(name)) lines.push("windowed by the trace's start time: scope by trace id with a window that contains the session's start");
    return lines;
}

// Entities that only answer to a search term (the text search views).
export function needsTerm(entityName) {
    return Boolean(TEXT_VIEWS[short(entityName).toUpperCase()]);
}

export function alreadyKnown(graph, anchor, entityName) {
    const held = [];
    for (const nb of neighbours(graph, anchor.key)) {
        if (nb.node.sources.some((s) => s.entity === entityName)) held.push(nb.node);
    }
    return held;
}
